import React from 'react';
import BigBtn from '../design/components/BigBtn';
import ThemePicker from '../design/components/ThemePicker';
import LeaveBtn from '../design/components/LeaveBtn';
import { playerStyleFor } from '../design/themes';
import { useIsDesktop } from '../hooks/useIsDesktop';

const MODE_LABELS = {
  'trump-card': { label: 'TRUMP CARD', glyph: '🃏', note: 'NEEDS 4 PLAYERS TO AUTO-START' },
  'arrange':    { label: 'ARRANGE',    glyph: '📊', note: 'HOST CONTROLS START' },
  'guess-spot': { label: 'GUESS THE SPOT', glyph: '🎯', note: 'HOST CONTROLS START' },
};

const WaitingScreen = ({
  players = [],
  gameMode = 'trump-card',
  isHost = false,
  gameId = '',
  onHostStartGame,
  onLeave,
}) => {
  const mode = MODE_LABELS[gameMode] || MODE_LABELS['trump-card'];
  const canStart = players.length >= 1;
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <div className="pen-screen" style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 32, gap: 20, overflow: 'auto' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 12,
              background: 'var(--c-yellow)',
              border: 'var(--border-ink)',
              boxShadow: 'var(--shadow-hard-sm)',
              display: 'grid', placeItems: 'center',
              fontSize: 26,
            }}>{mode.glyph}</div>
            <div>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: 20, letterSpacing: 0.5,
                color: 'var(--c-ink)', lineHeight: 1,
              }}>{mode.label}</div>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: 0.6,
                color: 'var(--c-ink)', opacity: 0.65, marginTop: 4,
              }}>{mode.note}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LeaveBtn onLeave={onLeave} />
            <ThemePicker />
          </div>
        </div>

        {gameId && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 16,
            padding: '24px 28px',
            background: 'var(--c-ink)',
            border: 'var(--border-ink)',
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-hard)',
            animation: 'penBounceIn 380ms 80ms var(--ease-pop) both',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: 1,
                color: 'var(--c-yellow)', opacity: 0.8, marginBottom: 6,
              }}>ROOM CODE — SHARE THIS</div>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 64, fontWeight: 700,
                letterSpacing: 10, color: 'var(--c-yellow)', lineHeight: 1,
              }}>{gameId.toString().toUpperCase()}</div>
            </div>
            <button
              onClick={() => navigator.clipboard?.writeText(gameId.toString().toUpperCase())}
              style={{
                fontFamily: 'var(--font-display)', fontSize: 14, letterSpacing: 0.5,
                background: 'var(--c-yellow)', color: 'var(--c-ink)',
                border: 'var(--border-ink-sm)', borderRadius: 10,
                padding: '12px 18px', cursor: 'pointer',
                boxShadow: 'var(--shadow-hard-sm)',
                flexShrink: 0,
              }}
            >COPY CODE</button>
          </div>
        )}

        <div style={{
          background: isHost ? 'var(--c-yellow)' : 'var(--c-cream)',
          border: 'var(--border-ink)',
          borderRadius: 'var(--radius-card)',
          boxShadow: 'var(--shadow-hard)',
          padding: '20px 24px',
        }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: 1.2,
            color: 'var(--c-ink)', opacity: 0.8, marginBottom: 6,
          }}>{isHost ? '👑 YOU ARE THE HOST' : '● WAITING FOR HOST'}</div>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 28, lineHeight: 1.1,
            color: 'var(--c-ink)',
          }}>{isHost ? "START WHEN YOU'RE READY" : 'LOBBY OPEN'}</div>
          <div style={{ marginTop: 10 }}>
            <div style={{
              display: 'inline-block',
              fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 700,
              padding: '6px 14px',
              background: 'var(--c-ink)', color: 'var(--c-yellow)',
              borderRadius: 6,
            }}>{players.length} PLAYER{players.length !== 1 ? 'S' : ''}</div>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0 }}>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: 0.8,
            color: 'var(--c-ink)', marginBottom: 12,
          }}>IN THE ROOM</div>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12,
          }}>
            {players.map((playerId, i) => {
              const palette = playerStyleFor(i);
              return (
                <div key={playerId} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '14px 16px',
                  background: 'var(--c-cream)',
                  border: 'var(--border-ink)',
                  borderRadius: 'var(--radius-btn)',
                  boxShadow: 'var(--shadow-hard-sm)',
                  animation: `penBounceIn 360ms ${i * 80}ms var(--ease-pop) both`,
                }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 999,
                    background: palette.color,
                    border: 'var(--border-ink-sm)',
                    display: 'grid', placeItems: 'center',
                    fontSize: 24, flexShrink: 0,
                  }}>{palette.emoji}</div>
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: 18,
                    color: 'var(--c-ink)', letterSpacing: 0.4,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    flex: 1, minWidth: 0,
                  }}>{playerId.toString().toUpperCase()}</div>
                </div>
              );
            })}
            {players.length === 0 && (
              <div style={{
                gridColumn: '1 / -1',
                fontFamily: 'var(--font-mono)', fontSize: 13, opacity: 0.55,
                color: 'var(--c-ink)', padding: '20px 0', textAlign: 'center',
              }}>Nobody here yet…</div>
            )}
          </div>
        </div>

        <div>
          {isHost && (gameMode === 'arrange' || gameMode === 'guess-spot') ? (
            <BigBtn
              onClick={onHostStartGame}
              primary
              disabled={!canStart}
            >
              {canStart
                ? `✶ START GAME · ${players.length} PLAYER${players.length !== 1 ? 'S' : ''} →`
                : 'WAITING FOR PLAYERS…'}
            </BigBtn>
          ) : (
            <BigBtn disabled>
              {gameMode === 'trump-card'
                ? `WAITING FOR 4 PLAYERS · ${players.length}/4`
                : '⏳ WAITING FOR HOST…'}
            </BigBtn>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pen-screen" style={{ paddingBottom: 24 }}>
      {/* Brand bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px 6px', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'var(--c-yellow)',
            border: 'var(--border-ink)',
            boxShadow: 'var(--shadow-hard-sm)',
            display: 'grid', placeItems: 'center',
            fontSize: 18,
          }}>{mode.glyph}</div>
          <div>
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: 0.5,
              color: 'var(--c-ink)',
            }}>{mode.label}</div>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: 0.6,
              color: 'var(--c-ink)', opacity: 0.65,
            }}>{mode.note}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LeaveBtn onLeave={onLeave} />
          <ThemePicker />
        </div>
      </div>

      {/* Status hero */}
      <div style={{ padding: '12px 16px 6px' }}>
        <div style={{
          background: isHost ? 'var(--c-yellow)' : 'var(--c-cream)',
          border: 'var(--border-ink)',
          borderRadius: 'var(--radius-card)',
          boxShadow: 'var(--shadow-hard)',
          padding: '16px 18px',
          animation: 'penBounceIn 420ms var(--ease-pop) both',
        }}>
          {isHost ? (
            <>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 1.2,
                color: 'var(--c-ink)', opacity: 0.8, marginBottom: 4,
              }}>👑 YOU ARE THE HOST</div>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: 22, lineHeight: 1.1,
                color: 'var(--c-ink)',
              }}>START WHEN<br/>YOU'RE READY</div>
            </>
          ) : (
            <>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 1.2,
                color: 'var(--c-ink)', opacity: 0.8, marginBottom: 4,
              }}>● WAITING FOR HOST</div>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: 22, lineHeight: 1.1,
                color: 'var(--c-ink)',
              }}>LOBBY<br/>OPEN</div>
            </>
          )}
          <div style={{
            marginTop: 10, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
              padding: '4px 10px',
              background: 'var(--c-ink)', color: 'var(--c-yellow)',
              borderRadius: 6,
            }}>{players.length} PLAYER{players.length !== 1 ? 'S' : ''}</div>
            {/* animated dots for "waiting" signal */}
            {!isHost && (
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 18, letterSpacing: 6,
                color: 'var(--c-ink)', opacity: 0.55,
              }}>···</div>
            )}
          </div>
        </div>
      </div>

      {/* Game ID share strip */}
      {gameId && (
        <div style={{ padding: '10px 16px 0' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px',
            background: 'var(--c-ink)',
            border: 'var(--border-ink)',
            borderRadius: 'var(--radius-btn)',
            boxShadow: 'var(--shadow-hard)',
            animation: 'penBounceIn 380ms 80ms var(--ease-pop) both',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: 1,
                color: 'var(--c-yellow)', opacity: 0.8, marginBottom: 2,
              }}>ROOM CODE — SHARE THIS</div>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 700,
                letterSpacing: 4, color: 'var(--c-yellow)', lineHeight: 1,
              }}>{gameId.toString().toUpperCase()}</div>
            </div>
            <button
              onClick={() => navigator.clipboard?.writeText(gameId.toString().toUpperCase())}
              style={{
                fontFamily: 'var(--font-display)', fontSize: 10, letterSpacing: 0.5,
                background: 'var(--c-yellow)', color: 'var(--c-ink)',
                border: 'var(--border-ink-sm)', borderRadius: 7,
                padding: '6px 10px', cursor: 'pointer',
                boxShadow: 'var(--shadow-hard-sm)',
                flexShrink: 0,
              }}
            >COPY</button>
          </div>
        </div>
      )}

      {/* Player chips */}
      <div style={{ padding: '14px 16px 4px' }}>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 0.8,
          color: 'var(--c-ink)', marginBottom: 10,
        }}>IN THE ROOM</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {players.map((playerId, i) => {
            const palette = playerStyleFor(i);
            return (
              <div key={playerId} style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '8px 12px',
                background: 'var(--c-cream)',
                border: 'var(--border-ink)',
                borderRadius: 'var(--radius-btn)',
                boxShadow: 'var(--shadow-hard-sm)',
                animation: `penBounceIn 360ms ${i * 80}ms var(--ease-pop) both`,
              }}>
                <div style={{
                  width: 26, height: 26, borderRadius: 999,
                  background: palette.color,
                  border: 'var(--border-ink-sm)',
                  display: 'grid', placeItems: 'center',
                  fontSize: 14, flexShrink: 0,
                }}>{palette.emoji}</div>
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: 13,
                  color: 'var(--c-ink)', letterSpacing: 0.4,
                  maxWidth: 120,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{playerId.toString().toUpperCase()}</div>
              </div>
            );
          })}

          {/* empty-slot hint */}
          {players.length === 0 && (
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 11, opacity: 0.55,
              color: 'var(--c-ink)', padding: '8px 0',
            }}>Nobody here yet…</div>
          )}
        </div>
      </div>

      {/* Host CTA or non-host hint */}
      <div style={{ padding: '20px 16px 16px', display: 'flex', gap: 8 }}>
        {isHost && (gameMode === 'arrange' || gameMode === 'guess-spot') ? (
          <BigBtn
            onClick={onHostStartGame}
            primary
            disabled={!canStart}
          >
            {canStart
              ? `✶ START GAME · ${players.length} PLAYER${players.length !== 1 ? 'S' : ''} →`
              : 'WAITING FOR PLAYERS…'}
          </BigBtn>
        ) : (
          <BigBtn disabled>
            {gameMode === 'trump-card'
              ? `WAITING FOR 4 PLAYERS · ${players.length}/4`
              : '⏳ WAITING FOR HOST…'}
          </BigBtn>
        )}
      </div>

    </div>
  );
};

export default WaitingScreen;
